import { createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "../../../Services/axiosInterceptor";
import { Base_Url } from "../../../../Base-Url/Base_Url";

const SERVICE_CHARGE_TYPE_ID = {
  RECURRING: 1,
  ONE_OFF: 2,
};

// This route runs unauthenticated, so identity values come from the
// GetQuoteModel response (webProposal.quoteModel) rather than app auth state.
const buildServicesPayload = (
  {
    organisationKeyID,
    userKeyID,
    quoteKeyID,
    clientID,
    quoteTypeID,
    servicePackageIDs,
  },
  serviceChargeTypeID,
) => ({
  organisationKeyID,
  userKeyID,
  moduleKeyID: quoteKeyID,
  ClientKeyID: String(clientID),
  QuoteTypeID: quoteTypeID,
  ServiceChargeTypeID: serviceChargeTypeID,
  moduleName: "Quotation",
  ProfessionTypeIDs: null,
  BusinessTypeIDs: null,
  BusinessNatureIDs: null,
  ServicePackageIDs: servicePackageIDs || [],
  QuoteKeyID: null,
  SourceID: null,
});

const fetchServicesByChargeType = async (
  serviceChargeTypeID,
  quoteModel,
  thunkAPI,
) => {
  try {
    const res = await apiClient.post(
      `${Base_Url}/configure/Services/GetServicesWithGlobalPricingDriverListByServiceChargeType`,
      buildServicesPayload(quoteModel, serviceChargeTypeID),
    );

    return res.data?.responseData?.data || [];
  } catch (err) {
    return thunkAPI.rejectWithValue(
      err?.response?.data || "Something went wrong",
    );
  }
};

export const getRecurringServices = createAsyncThunk(
  "webProposalServices/getRecurringServices",
  (params, thunkAPI) =>
    fetchServicesByChargeType(
      SERVICE_CHARGE_TYPE_ID.RECURRING,
      params,
      thunkAPI,
    ),
);

export const getOneOffServices = createAsyncThunk(
  "webProposalServices/getOneOffServices",
  (params, thunkAPI) =>
    fetchServicesByChargeType(SERVICE_CHARGE_TYPE_ID.ONE_OFF, params, thunkAPI),
);

const CALCULATE_URL = `${Base_Url}/configure/Services/GetCalculatedServicesPriceByPackages`;

const toResult = (responseData) => ({
  prices: responseData.data || [],
  vatPercentage: responseData.vatPercentage || 0,
  currencyID: responseData.currencyID || 1,
  packageList: responseData.packageList || [],
  // Package quotes resolve each service's per-package price from this list
  // rather than the flat packageOneValue/Two/ThreeValue on `data` — see
  // packagePriceViaMapping in ProposalPricingTableStep.jsx.
  serviceMappingWithPackagesList: responseData.serviceMappingWithPackagesList || [],
});

export const getCalculatedServicesPriceByPackages = createAsyncThunk(
  "webProposalServices/getCalculatedServicesPriceByPackages",
  async (payload, thunkAPI) => {
    try {
      // One-off services must never reprice when the recurring payment
      // frequency changes, but the backend scales every row in a shared
      // calculateServicesGPDList by the single GetValueOf sent. So one-off
      // rows go in their own request pinned to GetValueOf: "Yearly", while
      // the recurring request scales normally.
      const { oneOffGetValueOf, calculateServicesGPDList = [], ...rest } =
        payload;
      // Additional Information pricing driver rows carry no
      // serviceChargeTypeID and can feed both recurring and one-off
      // formulas, so they need to be resent with both split requests.
      const oneOffEntries = calculateServicesGPDList.filter(
        (entry) => Number(entry.serviceChargeTypeID) === 2,
      );
      const recurringEntries = calculateServicesGPDList.filter(
        (entry) => Number(entry.serviceChargeTypeID) !== 2,
      );
      const additionalEntries = calculateServicesGPDList.filter(
        (entry) => entry.serviceChargeTypeID == null,
      );

      // recurringEntries also catches the Additional Information rows
      // (serviceChargeTypeID: null, since Number(null) !== 2) alongside any
      // genuine recurring service rows — so when every recurring service has
      // been removed, recurringEntries shrinks down to exactly
      // additionalEntries. Requiring recurringEntries.length to exceed
      // additionalEntries.length here meant "no recurring services left"
      // silently disabled the split entirely, sending one-off services
      // through in the single combined request using the recurring
      // section's own GetValueOf (whatever payment frequency it's set to)
      // instead of the Yearly pin — one-off prices then rose or fell with a
      // payment frequency they're not supposed to depend on at all.
      // Splitting only ever needs oneOffEntries to actually exist and need a
      // different GetValueOf; whether any recurring entries exist is a
      // separate concern (handled below by skipping that request entirely).
      const canSplit =
        oneOffGetValueOf &&
        oneOffGetValueOf !== payload.GetValueOf &&
        oneOffEntries.length > 0;

      if (!canSplit) {
        const res = await apiClient.post(CALCULATE_URL, payload);
        return toResult(res.data?.responseData || {});
      }

      // No genuine recurring service left to price (recurringEntries is
      // empty or additional-information-only) — skip the recurring request
      // instead of sending calculateServicesGPDList: [] to the backend.
      const hasRecurringServiceEntries = recurringEntries.some(
        (entry) => Number(entry.serviceChargeTypeID) === 1,
      );

      const [recurringRes, oneOffRes] = await Promise.all([
        hasRecurringServiceEntries
          ? apiClient.post(CALCULATE_URL, {
              ...rest,
              calculateServicesGPDList: recurringEntries,
            })
          : Promise.resolve(null),
        apiClient.post(CALCULATE_URL, {
          ...rest,
          GetValueOf: oneOffGetValueOf,
          calculateServicesGPDList: [...oneOffEntries, ...additionalEntries],
        }),
      ]);

      const recurringResult = recurringRes
        ? toResult(recurringRes.data?.responseData || {})
        : null;
      const oneOffResult = toResult(oneOffRes.data?.responseData || {});

      return {
        // vatPercentage/currencyID/packageList come from whichever request
        // actually ran — with no recurring services left, the recurring
        // request is skipped entirely (see hasRecurringServiceEntries
        // above), so these must fall back to the one-off result instead of
        // toResult({})'s empty defaults.
        ...(recurringResult || oneOffResult),
        prices: [
          ...(recurringResult?.prices || []).filter(
            (item) => Number(item.serviceChargeTypeID) !== 2,
          ),
          ...oneOffResult.prices.filter(
            (item) => Number(item.serviceChargeTypeID) === 2,
          ),
        ],
        serviceMappingWithPackagesList: [
          ...(recurringResult?.serviceMappingWithPackagesList || []).filter(
            (row) => Number(row.serviceChargeTypeID) !== 2,
          ),
          ...oneOffResult.serviceMappingWithPackagesList.filter(
            (row) => Number(row.serviceChargeTypeID) === 2,
          ),
        ],
      };
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err?.response?.data || "Something went wrong",
      );
    }
  },
);
